import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { useVisibleModules } from '@/hooks/use-visible-modules';
import type { NavItem } from '@/lib/navigation';
import {
  DEFAULT_WORKSPACE,
  filterByWorkspace,
  getWorkspace,
  resolveWorkspace,
  type Workspace,
  type WorkspaceId,
} from '@/lib/workspaces';
import { useWorkspaceStore } from '@/stores/workspace-store';

interface WorkspaceContext {
  /** Só administradores (perfil `admin` ou superadmin) alternam entre áreas. */
  enabled: boolean;
  /** Área gravada para o usuário (pode diferir de `workspace` ao abrir um módulo de outra área). */
  selected: WorkspaceId;
  workspace: Workspace;
  /** Módulos da sidebar/menu "Mais": recortados pela área quando `enabled`. */
  modules: NavItem[];
  setWorkspace: (id: WorkspaceId) => void;
}

export function useWorkspace(): WorkspaceContext {
  const { user, roles, isSuperAdmin } = useAuth();
  const { pathname } = useLocation();
  const visible = useVisibleModules();
  const userId = user?.id ?? '';
  const selected = useWorkspaceStore((s) => s.byUser[userId] ?? DEFAULT_WORKSPACE);
  const store = useWorkspaceStore((s) => s.setWorkspace);
  const enabled = isSuperAdmin || roles.some((r) => r.code === 'admin');
  const setWorkspace = useCallback((id: WorkspaceId) => store(userId, id), [store, userId]);

  if (!enabled) {
    const workspace = getWorkspace(DEFAULT_WORKSPACE);
    return { enabled, selected, workspace, modules: visible, setWorkspace };
  }
  const workspace = resolveWorkspace(selected, pathname);
  const modules = filterByWorkspace(visible, workspace);
  return { enabled, selected, workspace, modules, setWorkspace };
}
